import { queryExecutor } from '@/_core/cqrs';
import {
    StorymapCardAiAdHocQuery,
    StorymapCardAiRefineQuery,
    StorymapCardCustomAiTemplateQuery,
} from '@/api/storymapCardDetails/queries';
import { AiProvider, AiExecutionContext, AiResult, AiMenuItemDef } from '@/pages/components/richTextEditor/types';
import { CustomAiStoryTemplate, StorymapAiTemplateOverride } from '@/pages/main/storymap/store/types';
import { snackbar } from '@/_core/app-components/use';
import { uuid } from '@/_core/utils/uuid';
import { useStorymapStore } from '../useModule';
import { ChangeCustomAiStoryTemplate } from '../actions/changeCustomAiStoryTemplate';
import { SaveNewCustomAiStoryTemplate } from '../actions/saveNewCustomAiStoryTemplate';

async function executeAdHoc(
    cardId: string,
    storymapId: string,
    ctx: AiExecutionContext
): Promise<AiResult> {
    try {
        const res = await queryExecutor.executeQuery(
            new StorymapCardAiAdHocQuery(cardId, storymapId, ctx.editorContentAsMarkdown, ctx.userInput)
        );
        if (res.IsSuccess) {
            return {
                isSuccess: true,
                text: res.SuggestedText,
                statusCode: res.StatusCode,
                request: ctx.userInput,
                promptType: res.PromptType,
            };
        } else {
            return {
                isSuccess: false,
                errorMessage: res.Error,
                statusCode: res.StatusCode,
                request: ctx.userInput,
            };
        }
    } catch (e) {
        return { isSuccess: false, request: ctx.userInput, errorMessage: 'Request failed.' };
    }
}

async function executeRefine(
    cardId: string,
    storymapId: string,
    ctx: AiExecutionContext
): Promise<AiResult> {
    try {
        const res = await queryExecutor.executeQuery(
            new StorymapCardAiRefineQuery(cardId, storymapId, ctx.editorContentAsMarkdown, ctx.userInput, ctx.conversationHistory)
        );
        if (res.IsSuccess) {
            return {
                isSuccess: true,
                text: res.SuggestedText,
                statusCode: res.StatusCode,
                request: ctx.userInput,
                promptType: res.PromptType,
            };
        } else {
            return {
                isSuccess: false,
                errorMessage: res.Error,
                statusCode: res.StatusCode,
                request: ctx.userInput,
            };
        }
    } catch (e) {
        return { isSuccess: false, request: ctx.userInput, errorMessage: 'Request failed.' };
    }
}

async function executeCustomTemplate(
    cardId: string,
    storymapId: string,
    templateId: string,
    templateName: string,
    ctx: AiExecutionContext
): Promise<AiResult> {
    try {
        const res = await queryExecutor.executeQuery(
            new StorymapCardCustomAiTemplateQuery(cardId, storymapId, templateId, ctx.editorContentAsMarkdown)
        );
        if (res.IsSuccess) {
            return {
                isSuccess: true,
                text: res.SuggestedText,
                statusCode: res.StatusCode,
                request: templateName,
                promptType: res.PromptType,
                templateId: templateId,
            };
        } else {
            return {
                isSuccess: false,
                errorMessage: res.Error,
                statusCode: res.StatusCode,
                request: templateName,
            };
        }
    } catch (e) {
        return { isSuccess: false, request: templateName, errorMessage: 'Request failed.' };
    }
}

async function executeLibraryTemplate(
    cardId: string,
    storymapId: string,
    templateId: string,
    templateName: string,
    ctx: AiExecutionContext
): Promise<AiResult> {
    try {
        const res = await queryExecutor.executeQuery(
            new StorymapCardCustomAiTemplateQuery(cardId, storymapId, templateId, ctx.editorContentAsMarkdown, true)
        );
        if (res.IsSuccess) {
            return {
                isSuccess: true,
                text: res.SuggestedText,
                statusCode: res.StatusCode,
                request: templateName,
                promptType: res.PromptType,
                templateId: templateId,
            };
        } else {
            return {
                isSuccess: false,
                errorMessage: res.Error,
                statusCode: res.StatusCode,
                request: templateName,
            };
        }
    } catch (e) {
        return { isSuccess: false, request: templateName, errorMessage: 'Request failed.' };
    }
}

export function createStorymapCardAiProvider(
    cardId: string,
    storymapId: string,
    getCustomTemplates: () => CustomAiStoryTemplate[],
    getLibraryTemplates: () => CustomAiStoryTemplate[],
    getOverrides: () => StorymapAiTemplateOverride[],
    isSampleStorymap: boolean,
    hasTemplateManagement: boolean
): AiProvider {
    return {
        get menuItems(): AiMenuItemDef[] {
            if (isSampleStorymap) return [];
            const overrides = getOverrides();
            return getLibraryTemplates()
                .filter((t) => t.target === 0)
                .filter((t) => {
                    const override = overrides.find(
                        (o) => o.libraryTemplateId === t.id
                    );
                    return override ? override.isVisible : t.isVisibleByDefault;
                })
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((t) => ({
                    title: t.name,
                    icon: '$fasSparkles',
                    execute: (ctx) => executeLibraryTemplate(
                        cardId, storymapId, t.id, t.name, ctx
                    ),
                    templateId: t.id,
                    templateBody: t.body,
                    templateType: 'library' as const,
                }));
        },
        get customMenuItems(): AiMenuItemDef[] {
            return getCustomTemplates()
                .filter((t) => t.target === 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((t) => ({
                    title: t.name,
                    icon: '$fasSparkles',
                    execute: (ctx) => executeCustomTemplate(cardId, storymapId, t.id, t.name, ctx),
                    templateId: t.id,
                    templateBody: t.body,
                    templateType: 'custom' as const,
                }));
        },
        executeAdHoc: (ctx) => executeAdHoc(cardId, storymapId, ctx),
        executeRefine: (ctx) => executeRefine(cardId, storymapId, ctx),
        ...(hasTemplateManagement ? {
            onUpdateTemplate: async (templateId: string, body: string): Promise<boolean> => {
                try {
                    const template = getCustomTemplates().find(t => t.id === templateId);
                    if (!template) {
                        snackbar.showError('Template not found');
                        return false;
                    }
                    return await useStorymapStore().dispatch(
                        new ChangeCustomAiStoryTemplate(
                            template.id, template.name, body, template.target, template.isVisibleByDefault
                        )
                    );
                } catch {
                    snackbar.showError('Failed to save template');
                    return false;
                }
            },
            onSaveAsTemplate: async (name: string, body: string): Promise<boolean> => {
                try {
                    const id = await uuid.create();
                    return await useStorymapStore().dispatch(
                        new SaveNewCustomAiStoryTemplate(id, name, body, 0, false)
                    );
                } catch {
                    snackbar.showError('Failed to save template');
                    return false;
                }
            },
        } : {}),
    };
}
